import React, { useEffect, useState } from 'react';
import Drawer from '../UI/Drawer';
import { errMessage, inputCls, inputStyle, primaryBtn, primaryBtnStyle, secondaryBtn, secondaryBtnStyle } from '../UI/ink';
import { integrationsApi } from '../../api/integrations';
import { Notice, Switch } from './ui';

const COPY = {
  ollama: { kicker: 'INTEGRATION · AI MODEL · LOCAL', title: 'Ollama', sub: 'First choice for the assistant, triage and investigations. Nothing leaves your network.' },
  claude: { kicker: 'INTEGRATION · AI MODEL · EXTERNAL', title: 'Claude', sub: 'Used when no Ollama is set up. Prompts go to Anthropic.' },
};

// Configure Ollama (URL and model) or Claude (API key and model). A saved
// key is never shown: leave the field blank to keep it.
export default function ModelDrawer({ provider, onClose, onChanged }) {
  const open = !!provider;
  const [enabled, setEnabled] = useState(true);
  const [url, setUrl] = useState('');
  const [model, setModel] = useState('');
  const [key, setKey] = useState('');
  const [saved, setSaved] = useState(null);
  const [models, setModels] = useState([]);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setError(null); setResult(null); setKey(''); setModels([]);
    integrationsApi.get(provider).then((it) => {
      setSaved(it);
      setEnabled(it ? it.enabled : true);
      setUrl(it?.config?.url || (provider === 'ollama' ? 'http://localhost:11434' : ''));
      setModel(it?.config?.model || (provider === 'ollama' ? 'llama3' : ''));
    }).catch(err => setError(errMessage(err, 'Could not load this integration.')));
  }, [open, provider]);
  if (!open) return null;

  const keySet = saved?.config?.api_key?.set;
  const run = async (k, fn) => {
    setBusy(k); setError(null); setResult(null);
    try { await fn(); } catch (err) { setError(errMessage(err, 'Something went wrong.')); } finally { setBusy(null); }
  };
  const test = () => run('test', async () => {
    const r = provider === 'ollama' ? await integrationsApi.testOllama(url) : await integrationsApi.testClaude(key, model || undefined);
    setResult(r);
    if (r.success && r.models) setModels(r.models.map(m => m.name));
    onChanged?.();
  });
  const save = () => run('save', async () => {
    const config = provider === 'ollama' ? { url: url.trim(), model: model.trim() } : { api_key: key.trim(), model: model.trim() };
    await integrationsApi.save(provider, config, enabled);
    onChanged?.();
    onClose();
  });
  const c = COPY[provider];
  const canSave = provider === 'ollama' ? url.trim() : (key.trim() || keySet);

  return (
    <Drawer open={open} onClose={onClose} kicker={c.kicker} title={c.title} subtitle={c.sub}
      footer={<><span /><div className="flex gap-2"><button type="button" onClick={test} disabled={!!busy || !canSave} className={secondaryBtn} style={secondaryBtnStyle}>{busy === 'test' ? 'Testing…' : 'Test'}</button><button type="button" onClick={save} disabled={!!busy || !canSave} className={primaryBtn} style={primaryBtnStyle}>{busy === 'save' ? 'Saving…' : 'Save'}</button></div></>}>
      <div className="flex flex-col gap-5">
        {error && <Notice>{error}</Notice>}
        {result && (result.success
          ? <Notice kind="ok">{provider === 'ollama' ? `Connected${result.latency_ms != null ? ` in ${result.latency_ms} ms` : ''} · ${models.length} models` : `Key works · ${result.model}`}</Notice>
          : <Notice>{result.error}</Notice>)}
        {provider === 'ollama' ? (
          <label className="flex flex-col gap-1.5 text-[13px] font-medium">Server URL
            <input type="url" value={url} onChange={e => setUrl(e.target.value)} className={`${inputCls} font-mono`} style={inputStyle} placeholder="http://10.0.9.5:11434" />
            <span className="font-normal text-xs" style={{ color: 'var(--ink-faint)' }}>The Worker calls this address, so it must be reachable from Cloudflare, e.g. through a tunnel.</span>
          </label>
        ) : (
          <label className="flex flex-col gap-1.5 text-[13px] font-medium">API key
            <input type="password" autoComplete="off" value={key} onChange={e => setKey(e.target.value)} className={`${inputCls} font-mono`} style={inputStyle} placeholder={keySet ? `Saved (ends ${saved.config.api_key.last4}). Leave blank to keep it.` : 'sk-ant-…'} />
            <span className="font-normal text-xs" style={{ color: 'var(--ink-faint)' }}>Stored encrypted and never shown again.</span>
          </label>
        )}
        <label className="flex flex-col gap-1.5 text-[13px] font-medium">Model
          {models.length > 0
            ? <select value={model} onChange={e => setModel(e.target.value)} className={inputCls} style={inputStyle}>{models.map(m => <option key={m} value={m}>{m}</option>)}</select>
            : <input type="text" value={model} onChange={e => setModel(e.target.value)} className={`${inputCls} font-mono`} style={inputStyle} placeholder={provider === 'ollama' ? 'llama3' : 'claude-sonnet-5-5'} />}
        </label>
        <div className="flex items-center gap-3 px-3.5 py-3 rounded-[10px] border" style={{ borderColor: 'var(--surf-line)' }}>
          <span className="flex-grow flex flex-col gap-0.5"><span className="text-[13.5px] font-medium">Use {c.title}</span><span className="text-xs" style={{ color: 'var(--ink-faint)' }}>When off, DragonEye skips it and tries the next model.</span></span>
          <Switch on={enabled} onChange={setEnabled} label={`Use ${c.title}`} />
        </div>
      </div>
    </Drawer>
  );
}
